import { FC, useCallback } from "react";
import { TestimonialSection } from "./component/TestimonialSection";
import { EnterpriseAuthContainer } from "./component/EnterpriseAuthContainer";
import { AuthHeader } from "./component/AuthHeader";
import { useFormik } from "formik";
import {
  emptyResetPasswordValue,
  resetPasswordSchema,
  ResetPasswordValues,
} from "./validation";
import { useNavigate, useSearchParams } from "react-router-dom";
import PasswordInput from "../../components/PasswordInput";
import Button from "../../components/Button";
import useFetch from "../../hooks/useFetch";
import { resetPasswordAPI } from "../../API/auth";
import { toast } from "react-toastify";

export const ResetPasswordPage: FC = () => {
  const navigate = useNavigate();
  const { isLoading, load } = useFetch();
  const token = useSearchParams()[0].get("token") || "";

  const handleResetPasswordButton = useCallback(
    async (values: ResetPasswordValues, resetForm: () => void) => {
      const payload = {
        password: values.password,
        confirmPassword: values.confirmPassword,
      };

      load(resetPasswordAPI(token, payload)).then(() => {
        toast.success("Password reset successfully");
        resetForm();

        navigate("/enterprise/signin");
      });
    },
    [load, navigate, token]
  );

  const {
    errors,
    handleChange,
    values,
    touched,
    handleSubmit,
    isValid,
    dirty,
    resetForm,
  } = useFormik({
    initialValues: emptyResetPasswordValue,
    validationSchema: resetPasswordSchema,
    onSubmit: async (): Promise<void> => {
      await handleResetPasswordButton(values, resetForm);
    },
  });

  const isButtonDisabled = !isValid || !dirty;

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <EnterpriseAuthContainer>
        <>
          <AuthHeader
            title="Sign Up"
            subtitle="Create an account to get started"
          />
          <form className="" onSubmit={handleSubmit}>
            <PasswordInput
              name="password"
              label="New Password"
              placeholder="••••••••••••"
              error={(touched?.password && errors?.password) || ""}
              value={values.password}
              onChange={handleChange}
            />
            <PasswordInput
              name="confirmPassword"
              label="Confirm Password"
              placeholder="••••••••••••"
              error={
                (touched?.confirmPassword && errors?.confirmPassword) || ""
              }
              value={values.confirmPassword}
              onChange={handleChange}
            />
            <Button
              text="Reset Password"
              className="w-full mt-6"
              type={isButtonDisabled ? "muted" : "primary"}
              disabled={isButtonDisabled || isLoading}
              isLoading={isLoading}
            />
          </form>
        </>
      </EnterpriseAuthContainer>

      <TestimonialSection />
    </div>
  );
};
